import { Injectable } from '@angular/core';

import type { ISponsorGeographyStepData } from '../models/sponsor-geography-step-data.interface';
import type { ISponsorPrimaryLocationApi } from '../models/sponsor-primary-location-api.interface';
import type { ISponsorPrimaryLocationDraft } from '../models/sponsor-primary-location-draft.interface';

@Injectable({
  providedIn: 'root',
})
export class SponsorGeographyStepService {
  public isPrimaryLocationDraftValid(location: ISponsorPrimaryLocationDraft): boolean {
    const city = location.city.trim();
    const country = location.country.trim();
    return city.length >= 2 && country.length >= 1;
  }

  public isGeographyDraftValid(geography: ISponsorGeographyStepData): boolean {
    if (!geography.geographic_radius) {
      return false;
    }
    return this.isPrimaryLocationDraftValid(geography.primary_location);
  }

  public buildStepPayload(geography: ISponsorGeographyStepData): Record<string, unknown> | null {
    if (!geography.geographic_radius) {
      return null;
    }
    const primaryLocation = this.buildPrimaryLocationApiPayload(geography.primary_location);
    if (!primaryLocation) {
      return null;
    }
    return {
      geographic_radius: geography.geographic_radius,
      primary_location: primaryLocation,
    };
  }

  private buildPrimaryLocationApiPayload(
    location: ISponsorPrimaryLocationDraft,
  ): ISponsorPrimaryLocationApi | null {
    if (!this.isPrimaryLocationDraftValid(location)) {
      return null;
    }

    const address = location.address.trim();
    const city = location.city.trim();
    const province = location.province.trim();
    const country = location.country.trim();
    const formattedAddress =
      this.toNullableTrimmedString(location.formatted_address) ??
      [address, city, province, country].filter((part) => part.length > 0).join(', ');

    return {
      formatted_address: formattedAddress,
      address,
      city,
      province: province.length > 0 ? province : null,
      region: this.toNullableTrimmedString(location.region),
      postal_code: this.toNullableTrimmedString(location.postal_code),
      country,
      latitude: location.latitude ?? null,
      longitude: location.longitude ?? null,
    };
  }

  private toNullableTrimmedString(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
